/**
 * <burnout-marquee>
 *
 * Seamless infinite ticker that can also be dragged.
 *
 * Clones its single content set once so the track can translate exactly -50%
 * and land back where it started, with no visible seam. The clone is inert and
 * aria-hidden, so screen readers and the tab order only ever see the original.
 *
 * Driven by the Web Animations API rather than a CSS animation. A CSS keyframe
 * can be paused but its position cannot be read or set, so there was no way to
 * let a finger move the strip and then carry on from that point. With an
 * Animation handle, currentTime is both readable and writable, which makes
 * drag-and-resume a few lines instead of a rewrite — and it still runs off the
 * compositor, so it costs no more than the CSS version did.
 *
 * Duration is derived from the measured width against a speed in pixels per
 * second, which keeps the scroll rate identical whether there are three claims
 * or six — a fixed duration would make a longer strip race.
 */
class BurnoutMarquee extends HTMLElement {
  #track = null;
  #set = null;
  #animation = null;
  #duration = 0;
  #width = 0;
  #drag = null;

  connectedCallback() {
    this.#track = this.querySelector('.bo-mq__track');
    this.#set = this.querySelector('.bo-mq__set');
    if (!this.#track || !this.#set) return;

    const clone = this.#set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    this.#track.appendChild(clone);

    this.#measure();
    this.#bindDrag();

    // Widths change with the viewport and once webfonts land, so re-derive
    // rather than trusting the first paint.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.#measure()).observe(this.#set);
    }
    document.fonts?.ready.then(() => this.#measure());

    // Off screen, stop entirely. An animation ticking in a section nobody is
    // looking at is pure battery cost on a phone.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          if (!this.#animation || this.#drag) return;
          entries[0]?.isIntersecting ? this.#animation.play() : this.#animation.pause();
        },
        { threshold: 0 }
      ).observe(this);
    }
  }

  get #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #measure = () => {
    const width = this.#set.scrollWidth;
    if (!width || this.#reduced) return;

    this.#width = width;
    const speed = Number(this.dataset.speed) || 60; // px per second
    this.#duration = (width / speed) * 1000;

    // Preserve position across a re-measure so a resize does not jump the strip
    // back to the start mid-scroll.
    const progress = this.#animation ? (Number(this.#animation.currentTime) || 0) / (this.#animation.effect?.getTiming().duration || 1) : 0;

    this.#animation?.cancel();

    // Stand the CSS fallback down now that the script is driving. Leaving both
    // running means two compositor animations for one strip, and the CSS one
    // would keep ticking while a drag has the WAAPI timeline paused.
    this.#track.style.animation = 'none';

    const reverse = this.classList.contains('bo-rv__row--reverse');
    this.#animation = this.#track.animate(
      [
        { transform: reverse ? 'translateX(-50%)' : 'translateX(0)' },
        { transform: reverse ? 'translateX(0)' : 'translateX(-50%)' },
      ],
      { duration: this.#duration, iterations: Infinity, easing: 'linear' }
    );

    this.#animation.currentTime = progress * this.#duration;
    if (this.#drag) this.#animation.pause();
  };

  /**
   * Finger drag.
   *
   * Only the horizontal intent is taken. The vertical axis is left to the page
   * so a swipe down the screen still scrolls — the thing that makes most
   * draggable strips infuriating on a phone is that they eat that gesture.
   */
  #bindDrag() {
    const el = this.#track;

    el.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.#animation) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.#drag = {
          x: event.clientX,
          y: event.clientY,
          axis: null,
          time: Number(this.#animation.currentTime) || 0,
        };
        this.#animation.pause();
      },
      { passive: true }
    );

    el.addEventListener(
      'pointermove',
      (event) => {
        const drag = this.#drag;
        if (!drag || !this.#animation || !this.#width) return;

        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;

        if (drag.axis === null) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          // A vertical intent hands the gesture straight back to the page.
          if (drag.axis === 'y') {
            this.#drag = null;
            this.#animation.play();
            return;
          }
        }

        if (drag.axis !== 'x') return;

        // Pixels to milliseconds: the track covers half its width over one
        // full duration, so that ratio converts a drag distance into time.
        const perPixel = this.#duration / (this.#width || 1);
        const reverse = this.classList.contains('bo-rv__row--reverse');
        const delta = (reverse ? dx : -dx) * perPixel;

        // Wrap rather than clamp, so dragging past either end continues
        // seamlessly instead of hitting a wall.
        const next = (((drag.time + delta) % this.#duration) + this.#duration) % this.#duration;
        this.#animation.currentTime = next;
      },
      { passive: true }
    );

    const release = () => {
      if (!this.#drag) return;
      this.#drag = null;
      // Carries on from wherever the finger left it.
      this.#animation?.play();
    };

    el.addEventListener('pointerup', release, { passive: true });

    // Desktop hover pause, which used to be a CSS play-state. A Web Animations
    // timeline is not reachable from CSS, so it moves here. Gated on a real
    // hover device so a tap on a phone cannot leave the strip parked.
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerenter', () => {
        if (!this.#drag) this.#animation?.pause();
      });
      this.addEventListener('pointerleave', () => {
        if (!this.#drag) this.#animation?.play();
      });
    }

    el.addEventListener('pointercancel', release, { passive: true });
    el.addEventListener('pointerleave', release, { passive: true });
  }
}

if (!customElements.get('burnout-marquee')) {
  customElements.define('burnout-marquee', BurnoutMarquee);
}
